import { ChevronDown, ChevronRight } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';

import {
  DEFAULT_SPECTRUM_PARAMS,
  type SpectrumParams,
} from '../../audio/spectrum';
import {
  DEFAULT_WAVEFORM_PARAMS,
  type ColorMode,
  type ReductionMode,
  type WaveformParams,
} from '../../audio/waveform-render';

type TweakPanelProps = {
  waveform: WaveformParams;
  spectrum: SpectrumParams;
  onWaveformChange(params: WaveformParams): void;
  onSpectrumChange(params: SpectrumParams): void;
};

/**
 * The render-parameter inspector carried over from the desktop player.
 *
 * Everything here is a draw-time transform over the stored analysis — nothing mutates
 * the baked data — so it costs nothing to offer to every visitor rather than gating it
 * behind the owner session. Collapsed by default so it stays out of the way.
 */
export function TweakPanel({
  waveform,
  spectrum,
  onWaveformChange,
  onSpectrumChange,
}: TweakPanelProps) {
  const [open, setOpen] = useState(false);
  const bodyId = useId();

  return (
    <section className="tweak">
      <button
        type="button"
        className="tweak-toggle"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen(!open)}
      >
        <span>Tweak</span>
        {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
      </button>

      {open && (
        <div className="tweak-body" id={bodyId}>
          <Section
            title="Waveform"
            hint="The shape of the whole set, analysed once on upload. It is drawn above and doubles as the seek bar. These are draw-time settings — the stored analysis never changes."
          >
            <Choice
              label="Reduction"
              value={waveform.mode}
              options={[
                ['rms', 'RMS'],
                ['peak', 'Peak'],
              ]}
              onChange={(mode) => onWaveformChange({ ...waveform, mode: mode as ReductionMode })}
            />
            <Choice
              label="Colour"
              value={waveform.colorMode}
              options={[
                ['spectral', 'Spectral'],
                ['solid', 'Solid'],
              ]}
              onChange={(colorMode) =>
                onWaveformChange({ ...waveform, colorMode: colorMode as ColorMode })
              }
            />
            <Slider
              label="Bars"
              value={waveform.bins}
              min={64}
              max={4_000}
              step={1}
              format={(value) => String(Math.round(value))}
              onChange={(bins) => onWaveformChange({ ...waveform, bins })}
            />
            <Slider
              label="Gamma"
              value={waveform.gamma}
              min={0.2}
              max={2}
              step={0.05}
              format={(value) => value.toFixed(2)}
              onChange={(gamma) => onWaveformChange({ ...waveform, gamma })}
            />
            <Slider
              label="Height"
              value={waveform.heightScale}
              min={0.2}
              max={3}
              step={0.05}
              format={(value) => `${value.toFixed(2)}x`}
              onChange={(heightScale) => onWaveformChange({ ...waveform, heightScale })}
            />
            <Slider
              label="Smoothing"
              value={waveform.smoothing}
              min={0}
              max={8}
              step={1}
              format={(value) => String(Math.round(value))}
              onChange={(smoothing) => onWaveformChange({ ...waveform, smoothing })}
            />
            <Toggle
              label="Mirror"
              checked={waveform.mirror}
              onChange={(mirror) => onWaveformChange({ ...waveform, mirror })}
            />
          </Section>

          <Section
            title="Spectrum analyser"
            hint="The live bars in the player bar at the bottom of the screen, showing only the instant that is sounding right now. Nothing to do with the waveform above."
          >
            <Slider
              label="Floor"
              value={spectrum.dbFloor}
              min={-120}
              max={-30}
              step={1}
              format={(value) => `${Math.round(value)} dB`}
              onChange={(dbFloor) => onSpectrumChange({ ...spectrum, dbFloor })}
            />
            <Slider
              label="Ceiling"
              value={spectrum.dbCeil}
              min={-60}
              max={0}
              step={1}
              format={(value) => `${Math.round(value)} dB`}
              onChange={(dbCeil) => onSpectrumChange({ ...spectrum, dbCeil })}
            />
            <Slider
              label="Sensitivity"
              value={spectrum.sensitivity}
              min={0.25}
              max={3}
              step={0.05}
              format={(value) => `${value.toFixed(2)}x`}
              onChange={(sensitivity) => onSpectrumChange({ ...spectrum, sensitivity })}
            />
            <Toggle
              label="Symmetric"
              checked={spectrum.symmetric}
              onChange={(symmetric) => onSpectrumChange({ ...spectrum, symmetric })}
            />
          </Section>

          <div className="tweak-actions">
            <button
              type="button"
              className="button"
              onClick={() => {
                onWaveformChange(DEFAULT_WAVEFORM_PARAMS);
                onSpectrumChange(DEFAULT_SPECTRUM_PARAMS);
              }}
            >
              Reset
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * One named group of controls.
 *
 * The panel drives two different visuals that are easy to confuse — the waveform is the
 * whole set's baked shape, the analyser is the live current instant — so each group
 * states which one it moves and where to look for it.
 */
function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint: string;
  children: ReactNode;
}) {
  return (
    <section className="tweak-section">
      <header className="tweak-section-head">
        <span className="label">{title}</span>
        <p>{hint}</p>
      </header>
      <div className="tweak-fields">{children}</div>
    </section>
  );
}

function Slider({
  label,
  value,
  min,
  max,
  step,
  format,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  format(value: number): string;
  onChange(value: number): void;
}) {
  return (
    <label className="tweak-field">
      <span className="tweak-field-head">
        <span className="label">{label}</span>
        <span className="numeric">{format(value)}</span>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
      />
    </label>
  );
}

function Choice({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: [string, string][];
  onChange(value: string): void;
}) {
  return (
    <div className="tweak-field">
      <span className="label">{label}</span>
      <div className="segmented" role="group" aria-label={label}>
        {options.map(([key, text]) => (
          <button
            key={key}
            type="button"
            className={value === key ? 'is-active' : ''}
            aria-pressed={value === key}
            onClick={() => onChange(key)}
          >
            {text}
          </button>
        ))}
      </div>
    </div>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange(checked: boolean): void;
}) {
  return (
    <div className="tweak-field">
      <span className="label">{label}</span>
      <div className="segmented" role="group" aria-label={label}>
        <button
          type="button"
          className={checked ? 'is-active' : ''}
          aria-pressed={checked}
          onClick={() => onChange(true)}
        >
          On
        </button>
        <button
          type="button"
          className={checked ? '' : 'is-active'}
          aria-pressed={!checked}
          onClick={() => onChange(false)}
        >
          Off
        </button>
      </div>
    </div>
  );
}
